import Card from "./Card";
import Button from "Components/Common/Button/Button";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import { DrawDetail, fmtMoney } from "../DrawHistory.types";
import { formatDate } from "utils/Common/Date";
import EmptyState from "Components/Common/EmptyState";
import NoDocuments from "Assets/Img/NoDocuments";

interface ApprovalTabProps {
  drawDetail: DrawDetail | null;
  loading: boolean;
  onExportG702: () => void;
  onExportExcel: () => void;
}

const ApprovalTab = ({
  drawDetail,
  loading,
  onExportG702,
  onExportExcel,
}: ApprovalTabProps) => {
  if (loading || !drawDetail) {
    return (
      <>
        <div className="sectionTitle">Approval &amp; Certification</div>
        <Skeleton
          width="65%"
          height={20}
          borderRadius={4}
          style={{ marginBottom: 14 }}
        />
        <div className="approvalBlock pending">
          <Skeleton width={44} height={44} borderRadius="50%" />
          <div className="approvalBody">
            <Skeleton
              width="50%"
              height={20}
              borderRadius={4}
              style={{ marginBottom: 8 }}
            />
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton
                key={i}
                width="70%"
                height={14}
                borderRadius={4}
                style={{ marginBottom: 6 }}
              />
            ))}
          </div>
        </div>

        <Card title="Certified Documents">
          {Array.from({ length: 2 }).map((_, i) => (
            <div className="docItem" key={i}>
              <Skeleton width={36} height={36} borderRadius={8} />
              <div className="docBody">
                <Skeleton
                  width="55%"
                  height={13}
                  borderRadius={4}
                  style={{ marginBottom: 6 }}
                />
              </div>
              <Skeleton width={90} height={28} borderRadius={5} />
            </div>
          ))}
        </Card>
      </>
    );
  }

  const approval = drawDetail.approval;
  const approvalDocuments = (approval as any)?.documents;
  const isApproved = approval?.isApproved === true;
  const drawNumberLabel = String(drawDetail.draw.drawNumber).padStart(2, "0");
  const recommendation = approval?.cscRecommendation;
  return (
    <>
      <div className="sectionTitle">Approval &amp; Certification</div>
      <div className="sectionSub">
        Final approval status for Draw #{drawNumberLabel}.
      </div>

      <div className={`approvalBlock${isApproved ? "" : " pending"}`}>
        <div className={`approvalIcon${isApproved ? " approved" : " pending"}`}>
          {isApproved ? "✓" : "⏳"}
        </div>
        <div className="approvalBody">
          <div
            className={`approvalTitle${isApproved ? " approved" : " pending"}`}
          >
            Draw #{drawNumberLabel} —{" "}
            {isApproved ? "Approved & Funded" : "Awaiting Bank Approval"}
          </div>
          <div className="approvalMeta">
            {isApproved && approval?.approvedBy && (
              <>
                <strong>Approved by:</strong>{" "}
                {approval.approvedBy.display ?? approval.approvedBy.name}
                <br />
              </>
            )}
            {isApproved && approval?.approvalDate && (
              <>
                <strong>Approval date:</strong>{" "}
                {formatDate(approval.approvalDate, false)}
                <br />
              </>
            )}
            <strong>Amount certified:</strong>{" "}
            {fmtMoney(
              approval?.amountCertified ??
                drawDetail.draw.approvedAmount ??
                drawDetail.draw.requestedAmount,
            )}
            <br />
            <strong>Funds disbursed:</strong>{" "}
            {approval?.fundsDisbursed?.date
              ? `${new Date(approval.fundsDisbursed.date).toLocaleDateString(
                  "en-US",
                  {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  },
                )} — wire transfer to GC operating account`
              : "—"}
            <br />
            {recommendation && (
              <>
                <strong>CSC recommendation:</strong> {recommendation.text}
                {recommendation.flagCount
                  ? ` (${recommendation.flagCount} flag${recommendation.flagCount === 1 ? "" : "s"})`
                  : ""}
                <br />
              </>
            )}
            {approval?.retainage && (
              <>
                <strong>Retainage held this draw:</strong>{" "}
                {fmtMoney(approval.retainage.thisDraw)}
                (cumulative retainage: {fmtMoney(approval.retainage.cumulative)}
                )
                <br />
              </>
            )}
            {approval?.bankNotes && (
              <>
                <strong>Bank notes:</strong> "{approval.bankNotes}"
              </>
            )}
          </div>
        </div>
      </div>

      {/* <Card title="Certified Documents">
        <div className="docItem">
          <div className="docIcon pdf">PDF</div>
          <div className="docBody">
            <div className="docName">AIA G702 — Draw #{drawNumberLabel}</div>
            <div className="docMeta">
              Application &amp; Certificate for Payment
            </div>
          </div>
          <Button type="button" className="docBtn" onClick={onExportG702}>
            Download
          </Button>
        </div>
        <div className="docItem">
          <div className="docIcon pdf">XLS</div>
          <div className="docBody">
            <div className="docName">AIA G703 — Draw #{drawNumberLabel}</div>
            <div className="docMeta">
              Continuation sheet with billed amounts
            </div>
          </div>
          <Button type="button" className="docBtn" onClick={onExportExcel}>
            Download
          </Button>
        </div>
      </Card> */}
      <Card title="Certified Documents">
        {approvalDocuments?.length ? (
          approvalDocuments.map(
            (
              doc: { id: any; fileName: string; url: string | URL | undefined },
              index: number,
            ) => (
              <div className="docItem" key={doc.id ?? index}>
                <div className="docIcon pdf">
                  {doc.fileName?.split(".").pop()?.toUpperCase() ?? "DOC"}
                </div>

                <div className="docBody">
                  <div className="docName">
                    {doc.fileName ?? `Document ${index + 1}`}
                  </div>
                </div>

                <Button
                  type="button"
                  className="docBtn"
                  onClick={() => window.open(doc.url, "_blank")}
                >
                  Download
                </Button>
              </div>
            ),
          )
        ) : (
          <EmptyState
            noBorder
            icon={<NoDocuments />}
            title={"No documents available."}
          />
        )}
      </Card>
    </>
  );
};

export default ApprovalTab;
